import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import OnboardingWizard, { useOnboarding } from '../components/Onboarding/OnboardingWizard';
import ProgressIndicator from '../components/ProgressIndicator';
import InteractiveCalendar from '../components/InteractiveCalendar';
import axios from 'axios';
import { Calendar, MapPin, ChevronRight, Bell, MessageSquare, AlertTriangle, Building, Users, Megaphone, ClipboardCheck, TrendingUp } from 'lucide-react';
import NotificationStatsWidget from '../components/NotificationStatsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardGestionnaire = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const { shouldShow: showOnboarding, completeOnboarding } = useOnboarding();
  const [assemblees, setAssemblees] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [visites, setVisites] = useState([]);
  const [stats, setStats] = useState({ buildings: 0, coproprietaires: 0, incidentsOuverts: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [agRes, incRes, convRes, annoncesRes, visitesRes, buildingsRes, coproRes] = await Promise.all([
          axios.get(`${API}/assemblees`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/annonces`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/visites`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/buildings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/coproprietaires`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        setAssemblees(agRes.data.slice(0, 4));
        setIncidents(incRes.data.slice(0, 4));
        setConversations(convRes.data.slice(0, 3));
        setAnnonces(Array.isArray(annoncesRes.data) ? annoncesRes.data.slice(0, 3) : []);
        setVisites(Array.isArray(visitesRes.data) ? visitesRes.data.filter(v => v.status !== 'completed').slice(0, 3) : []);
        
        // Calculate stats
        const openIncidents = incRes.data.filter(i => !['Résolu', 'Fermé'].includes(i.status)).length;
        setStats({
          buildings: Array.isArray(buildingsRes.data) ? buildingsRes.data.length : 0,
          coproprietaires: Array.isArray(coproRes.data) ? coproRes.data.length : 0,
          incidentsOuverts: openIncidents
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  const getStatusColor = (status) => {
    const colors = {
      'Nouveau': 'border-l-blue-500 bg-blue-50/50',
      'Non traité': 'border-l-red-500 bg-red-50/50',
      'En cours': 'border-l-amber-500 bg-amber-50/50',
      'Assigné': 'border-l-orange-500 bg-orange-50/50',
      'Pris en charge': 'border-l-purple-500 bg-purple-50/50',
      'Résolu': 'border-l-emerald-500 bg-emerald-50/50',
      'Fermé': 'border-l-slate-400 bg-slate-50'
    };
    return colors[status] || 'border-l-slate-300 bg-slate-50';
  };

  const getStatusDot = (status) => {
    const colors = {
      'Nouveau': 'bg-blue-500',
      'Non traité': 'bg-red-500',
      'En cours': 'bg-amber-500',
      'Assigné': 'bg-orange-500',
      'Pris en charge': 'bg-purple-500',
      'Résolu': 'bg-emerald-500',
      'Fermé': 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-300';
  };

  const getAGStatusBadge = (status) => {
    const badges = {
      'Publiée': 'bg-blue-50 text-blue-600 border border-blue-100',
      'Ouverte': 'bg-emerald-50 text-emerald-600 border border-emerald-100',
      'En cours': 'bg-amber-50 text-amber-600 border border-amber-100',
      'Passée': 'bg-slate-100 text-slate-500 border border-slate-200'
    };
    return badges[status] || 'bg-slate-100 text-slate-500';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title={`Bonjour ${user?.prenom || ''} 👋`}
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Onboarding Wizard for new users */}
      {showOnboarding && !isMobile && (
        <OnboardingWizard 
          onComplete={completeOnboarding}
          onSkip={completeOnboarding}
        />
      )}

      {/* Mobile Header */}
      {isMobile && (
        <MobileHeader 
          title={`Bonjour ${user?.prenom || ''} 👋`}
          onMenuClick={onMenuClick}
        />
      )}

      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`} data-testid="dashboard-gestionnaire">
        {/* Desktop Title */}
        {!isMobile && (
          <h1 className="text-xl md:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Accueil</h1>
        )}

        {/* Stats Cards - New Section */}
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          <div 
            onClick={() => navigate('/immeubles')}
            className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 md:p-4 cursor-pointer hover:shadow-md transition-all group"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 md:p-2.5 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                <Building className="text-blue-500" size={18} />
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-800">{stats.buildings}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Immeubles</p>
              </div>
            </div>
          </div>
          <div 
            onClick={() => navigate('/coproprietaires')}
            className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 md:p-4 cursor-pointer hover:shadow-md transition-all group"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 md:p-2.5 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors">
                <Users className="text-emerald-500" size={18} />
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-800">{stats.coproprietaires}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Copropriétaires</p>
              </div>
            </div>
          </div>
          <div 
            onClick={() => navigate('/incidents')}
            className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 md:p-4 cursor-pointer hover:shadow-md transition-all group"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 md:p-2.5 bg-amber-50 rounded-lg group-hover:bg-amber-100 transition-colors">
                <AlertTriangle className="text-amber-500" size={18} />
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-800">{stats.incidentsOuverts}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Incidents ouverts</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Colonne 1 : Assemblées Générales + Accès rapide */}
          <div className="space-y-4 md:space-y-6">
            {/* Assemblées Générales */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Assemblées Générales</h2>
                <button 
                  onClick={() => navigate('/assemblees')}
                  className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
                  data-testid="view-all-assemblees"
                >
                  <span>Voir tout</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="p-3 md:p-4 space-y-2 md:space-y-3">
              {assemblees.length === 0 ? (
                <div className="text-center py-6 md:py-8">
                  <Calendar className="mx-auto text-slate-200 mb-3" size={28} strokeWidth={1.5} />
                  <p className="text-slate-400 text-sm">Aucune assemblée</p>
                </div>
              ) : (
                assemblees.slice(0, isMobile ? 2 : 4).map((ag) => (
                  <div
                    key={ag.id}
                    onClick={() => navigate('/assemblees', { state: { selectedAG: ag.id } })}
                    className="p-3 md:p-4 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl transition-all cursor-pointer hover:shadow-sm group"
                    data-testid={`ag-card-${ag.id}`}
                  >
                    <div className="flex items-start justify-between mb-1.5 md:mb-2">
                      <div className="text-[10px] md:text-xs font-semibold text-[#00D3A8] uppercase tracking-wide">
                        {formatDate(ag.date)}
                      </div>
                      <span className={`text-[9px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full font-medium ${getAGStatusBadge(ag.status)}`}>
                        {ag.status}
                      </span>
                    </div>
                    <div className="font-medium text-slate-700 text-sm md:text-base group-hover:text-slate-900 transition-colors truncate">{ag.titre}</div>
                    <div className="text-xs md:text-sm text-slate-500 flex items-center mt-1.5 md:mt-2">
                      <MapPin size={12} className="mr-1 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{ag.lieu}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

            {/* Accès rapide - En bas à gauche */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 md:p-5 border-b border-slate-100">
                <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Accès rapide</h2>
              </div>
              <div className="p-3 md:p-4">
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <button
                    onClick={() => navigate('/incidents')}
                    className="p-3 md:p-4 border border-slate-100 rounded-xl hover:border-[#00D3A8] hover:bg-slate-50 transition-all text-center"
                    data-testid="quick-access-incidents"
                  >
                    <div className="text-xl md:text-2xl mb-1">🚨</div>
                    <div className="text-xs md:text-sm font-medium text-slate-700">Incidents</div>
                  </button>
                  <button
                    onClick={() => navigate('/messagerie')}
                    className="p-3 md:p-4 border border-slate-100 rounded-xl hover:border-[#00D3A8] hover:bg-slate-50 transition-all text-center"
                    data-testid="quick-access-messagerie"
                  >
                    <div className="text-xl md:text-2xl mb-1">💬</div>
                    <div className="text-xs md:text-sm font-medium text-slate-700">Messagerie</div>
                  </button>
                  <button
                    onClick={() => navigate('/assemblees')}
                    className="p-3 md:p-4 border border-slate-100 rounded-xl hover:border-[#00D3A8] hover:bg-slate-50 transition-all text-center"
                    data-testid="quick-access-assemblees"
                  >
                    <div className="text-xl md:text-2xl mb-1">📋</div>
                    <div className="text-xs md:text-sm font-medium text-slate-700">Assemblées</div>
                  </button>
                  <button
                    onClick={() => navigate('/annonces')}
                    className="p-3 md:p-4 border border-slate-100 rounded-xl hover:border-[#00D3A8] hover:bg-slate-50 transition-all text-center"
                    data-testid="quick-access-annonces"
                  >
                    <div className="text-xl md:text-2xl mb-1">📢</div>
                    <div className="text-xs md:text-sm font-medium text-slate-700">Annonces</div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne 2 : Calendrier interactif - Caché sur petit mobile */}
          <div className="hidden sm:block">
            <InteractiveCalendar />
          </div>

          {/* Colonne 3 : Incidents + Notifications */}
          <div className="space-y-4 md:space-y-6">
            {/* Derniers incidents */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 md:p-5 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Derniers incidents</h2>
                  <button 
                    onClick={() => navigate('/incidents')}
                    className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
                    data-testid="view-all-incidents"
                  >
                    <span>Voir tout</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              
              <div className="p-3 md:p-4 space-y-2">
                {incidents.length === 0 ? (
                  <div className="text-center py-4 md:py-6">
                    <AlertTriangle className="mx-auto text-slate-200 mb-2 md:mb-3" size={24} strokeWidth={1.5} />
                    <p className="text-slate-400 text-sm">Aucun incident</p>
                  </div>
                ) : (
                  incidents.slice(0, isMobile ? 3 : 4).map((incident) => (
                    <div
                      key={incident.id}
                      onClick={() => navigate('/incidents', { state: { selectedIncident: incident.id } })}
                      className={`p-2.5 md:p-3 border-l-4 ${getStatusColor(incident.status)} hover:bg-opacity-70 transition-all cursor-pointer rounded-r-lg`}
                      data-testid={`incident-card-${incident.id}`}
                    >
                      <div className="font-medium text-slate-700 text-xs md:text-sm truncate">{incident.titre}</div>
                      <div className="flex items-center space-x-1.5 mt-0.5 md:mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(incident.status)}`}></span>
                        <span className="text-[10px] md:text-xs text-slate-500">{incident.status}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 md:p-5 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Notifications</h2>
                  <button 
                    onClick={() => navigate('/messagerie')}
                    className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
                    data-testid="view-all-messages"
                  >
                    <span>Voir tout</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              
              <div className="p-3 md:p-4">
                {conversations.length === 0 ? (
                  <div className="text-center py-4 md:py-6">
                    <Bell className="mx-auto text-slate-200 mb-2 md:mb-3" size={24} strokeWidth={1.5} />
                    <p className="text-slate-400 text-sm">Aucune notification</p>
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    {conversations.slice(0, isMobile ? 2 : 3).map((conv) => (
                      <div 
                        key={conv.id} 
                        onClick={() => navigate('/messagerie', { state: { selectedConv: conv.id } })}
                        className="flex items-start space-x-2.5 md:space-x-3 p-2 -mx-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                        data-testid={`notification-${conv.id}`}
                      >
                        <div className="w-8 h-8 md:w-9 md:h-9 bg-[#00D3A8] rounded-full flex items-center justify-center flex-shrink-0">
                          <MessageSquare size={14} className="text-white" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs md:text-sm font-medium text-slate-700">Messagerie</div>
                          <div className="text-[10px] md:text-xs text-slate-400">
                            {conv.last_message ? new Date(conv.last_message.created_at).toLocaleDateString('fr-FR') : ''}
                          </div>
                          <div className="text-xs md:text-sm text-slate-500 mt-0.5 truncate">
                            {conv.participants?.map(p => `${p.prenom}`).join(', ') || conv.name}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section supplémentaire : Annonces + Visites + Stats Widget */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Widget Statistiques Notifications */}
          <NotificationStatsWidget token={token} />

          {/* Dernières Annonces */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Dernières annonces</h2>
                <button 
                  onClick={() => navigate('/annonces')}
                  className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
                  data-testid="view-all-annonces"
                >
                  <span>Voir tout</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="p-3 md:p-4 space-y-2 md:space-y-3">
              {annonces.length === 0 ? (
                <div className="text-center py-6 md:py-8">
                  <Megaphone className="mx-auto text-slate-200 mb-3" size={28} strokeWidth={1.5} />
                  <p className="text-slate-400 text-sm">Aucune annonce</p>
                </div>
              ) : (
                annonces.map((annonce) => (
                  <div
                    key={annonce.id}
                    onClick={() => navigate('/annonces')}
                    className="p-3 md:p-4 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl transition-all cursor-pointer hover:shadow-sm group"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className={`text-[9px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full font-medium ${
                        annonce.type === 'urgent' ? 'bg-red-50 text-red-600 border border-red-100' :
                        annonce.type === 'info' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                        'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {annonce.type || 'Info'}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {annonce.created_at ? new Date(annonce.created_at).toLocaleDateString('fr-FR') : ''}
                      </span>
                    </div>
                    <div className="font-medium text-slate-700 text-sm md:text-base group-hover:text-slate-900 transition-colors truncate">
                      {annonce.titre}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {annonce.contenu?.substring(0, 100)}...
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Prochaines Visites */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Visites à venir</h2>
                <button 
                  onClick={() => navigate('/visites')}
                  className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
                  data-testid="view-all-visites"
                >
                  <span>Voir tout</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="p-3 md:p-4 space-y-2 md:space-y-3">
              {visites.length === 0 ? (
                <div className="text-center py-6 md:py-8">
                  <ClipboardCheck className="mx-auto text-slate-200 mb-3" size={28} strokeWidth={1.5} />
                  <p className="text-slate-400 text-sm">Aucune visite planifiée</p>
                  <button
                    onClick={() => navigate('/visites')}
                    className="mt-3 text-xs text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                  >
                    Planifier une visite
                  </button>
                </div>
              ) : (
                visites.map((visite) => (
                  <div
                    key={visite.id}
                    onClick={() => navigate('/visites')}
                    className="p-3 md:p-4 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl transition-all cursor-pointer hover:shadow-sm group"
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="text-[10px] md:text-xs font-semibold text-[#00D3A8] uppercase tracking-wide">
                        {visite.date ? new Date(visite.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date à définir'}
                      </div>
                      <span className={`text-[9px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full font-medium ${
                        visite.status === 'scheduled' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                        visite.status === 'in_progress' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {visite.status === 'scheduled' ? 'Planifiée' : visite.status === 'in_progress' ? 'En cours' : visite.status}
                      </span>
                    </div>
                    <div className="font-medium text-slate-700 text-sm md:text-base group-hover:text-slate-900 transition-colors truncate">
                      {visite.building_name || 'Visite d\'immeuble'}
                    </div>
                    <div className="text-xs md:text-sm text-slate-500 flex items-center mt-1.5">
                      <MapPin size={12} className="mr-1 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{visite.building_address || 'Adresse non définie'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Section Indicateur de progression */}
        {!isMobile && (
          <ProgressIndicator token={token} variant="full" />
        )}
      </div>
    </>
  );
};

export default DashboardGestionnaire;
